/**
 * **The Ankiety tab's entry point.** One read of `GET /customer/{id}/surveys` becomes the chart
 * strip and one occurrence's full report — decided together, in one place, because both are
 * statements about the same frozen snapshots.
 *
 * Canon: `docs/design/ankiety-klienta-2026-09-16/szkice/kierunek-a.html`, the layout the owner
 * chose on 16 September 2026; the STYLING is direction B's, which is where the strip comes from.
 * The left-hand stream is `model/surveyStream`, over the same read and every form in it.
 *
 * ── It is built around ONE occurrence, and the occurrence chooses the form ────────────────────
 * The pane shows the report of the row the trainer pressed in the stream, so what this builds is
 * that occurrence's own form: every occurrence of it, in order, with the cells and the deltas the
 * report and the comparison read off. `occurrenceId: null` resolves to the newest ANSWERED
 * occurrence of any form, which is what the tab opens on.
 *
 * ── The range bar narrows the CHARTS and nothing else ────────────────────────────────────────
 * Owner, 16 September 2026. The stream is the whole history and a report is a report whatever the
 * range says, so `chartRange` reaches `buildSeries` and reaches nothing else. It used to filter the
 * columns themselves, which under this layout would mean a stream row the pane could not draw.
 *
 * ── A question is FROZEN at send, so the rows are a union and not a template ──────────────────
 * Each occurrence carries its own `questions[]`, taken when it went out. A form that gained a
 * question in week 9 has eight snapshots without it and four with it, and the honest picture is one
 * row that BEGINS in week 9 — a dashed plate across the earlier columns saying so — rather than
 * eight empty cells that read as a client who stopped answering. So the row order is the NEWEST
 * snapshot's order (what the form asks today) with keys only older snapshots carry appended after
 * it, and every row knows the first column it exists in.
 *
 * ── Three cell states, and they are not interchangeable ───────────────────────────────────────
 * `absent` — the question was not on this occurrence's form. Nobody was asked.
 * `blank`  — it was asked and not answered: a missed occurrence, or one field of a measurement.
 * `value`  — an answer.
 * Collapsing `absent` into `blank` is what turns "the question is new" into "the client is
 * slipping", which is the one reading a trainer must not be given by accident.
 *
 * ── The ordinal is counted, not read ─────────────────────────────────────────────────────────
 * "tydz. 12" is nowhere on the wire. It is counted from the earliest occurrence of the same FORM
 * in the fetched list, stepped by the form's own cadence (`SurveyCadenceMath` on the backend). The
 * read is capped at 200 occurrences with no `from`/`to`, so on a long history that earliest one is
 * not enrolment: the label is an ordinal **in view**, and `AGENTS.md` says so where a trainer's
 * question about it would be asked.
 *
 * ── The delta compares against the previous ANSWERED occurrence, never the previous column ────
 * A missed week does not reset a measurement to nothing and must not produce a delta against zero.
 * `null` is the only way to say "there is nothing to compare with", and it is what the first
 * reading of every row gets.
 *
 * Pure: no React, no DOM, no query hook, no i18next import. `t` and the locale arrive as
 * parameters.
 */

import type { ChartPoint } from '~/shared/lib/chartSeries';
import { clockDuration } from '~/shared/lib/clockDuration';
import { numberFormat } from '~/shared/lib/intlCache';
import { questionNames, standardOptionLabel } from '~/shared/lib/standardQuestionNames';

import { MS_PER_DAY, parseDateOnly, writtenCivilDate } from '~/shared/lib/calendarDate';
import { daysLate } from './lateness';
import { choiceListOf, type SurveyChoiceListView } from './surveyChoiceList';
import { HEIGHT_KEY, questionVital, SLEEP_CODE, WEIGHT_KEYS } from './surveyQuestionKeys';

type Translate = (key: string, options?: Record<string, unknown>) => string;

/* ── What `api/clientDetailSource` hands in ─────────────────────────────────────────────────── */

/** The contract's `AnswerType`, exactly. `null` for a word this build does not know. */
export type SurveyAnswerType =
  | 'ShortText'
  | 'LongText'
  | 'SingleChoice'
  | 'MultiChoice'
  | 'Number'
  | 'Scale'
  | 'Date'
  | 'GoalBuilder'
  | 'Measurements'
  | 'Photo'
  | 'ExerciseClip';

export type SurveyPhotoPose = 'front' | 'side' | 'back' | 'secondSide';

export type SurveyCadenceFrequency = 'Day' | 'Week' | 'Month';

export type SurveyOccurrenceStatus = 'OnTime' | 'Late' | 'Missed' | 'Cancelled';

/** An occurrence's outcome as a surface names it: the wire's four, plus a cycle still open. */
export type SurveyOccurrenceOutcome = SurveyOccurrenceStatus | 'Open';

/** The assignment states in which the client can still answer: delivered, or partly saved. */
const OPEN_ASSIGNMENT_STATUSES: readonly string[] = ['Sent', 'InProgress'];

/**
 * What became of one occurrence, decided once for every surface that prints it.
 *
 * The wire's `occurrenceStatus` wins wherever it is set. Where it is not, an answered occurrence
 * landed on time, and an unanswered one is OPEN only while it is its form's newest occurrence and
 * the client can still answer it. Reading every unanswered row as missed labelled today's check-in
 * „pominięta" from the moment the server's sweep minted it; reading every `Sent` row as open
 * labelled a three-week-old one „czeka" wherever the backend never closed it — a paused form, a
 * client no longer active. A newer occurrence of the same form means this cycle is over.
 *
 * `newestOfForm` is the caller's to know: whether no later occurrence of this form (its comparison
 * group) is in the read.
 */
export function occurrenceOutcomeOf(
  occurrence: SurveyOccurrenceSource,
  newestOfForm: boolean,
): SurveyOccurrenceOutcome {
  if (occurrence.occurrenceStatus !== null) return occurrence.occurrenceStatus;
  if (occurrence.status === 'Completed') return 'OnTime';
  if (
    newestOfForm &&
    occurrence.status !== null &&
    OPEN_ASSIGNMENT_STATUSES.includes(occurrence.status)
  ) {
    return 'Open';
  }
  return 'Missed';
}

export interface SurveyQuestionSource {
  readonly key: string;
  /**
   * The STANDARD catalog's code — `checkin_weight`, `checkin_sleep` — or `null` on a question the
   * trainer wrote themselves.
   *
   * It is what a coded question is NAMED from: the backend stores the catalog's English default in
   * `label` by design (`StandardQuestionCatalog.cs`) and expects every client to render
   * `intakeSurvey.q.<code>.head` instead. See `shared/lib/standardQuestionNames`.
   */
  readonly code: string | null;
  readonly label: string;
  readonly answerType: SurveyAnswerType | null;
  readonly options: readonly string[];
  /** Present on an `ExerciseClip` question. The NAME is resolved separately — see `deps.exerciseNames`. */
  readonly exerciseId: number | null;
  /**
   * The wire's `vitalType` — which of the client's readings the answer is (`SleepDuration`,
   * `EnergyLevel`). Absent on a question that is none, and on every snapshot frozen before the
   * field existed; `surveyQuestionKeys`' `questionVital` reads the code there instead.
   */
  readonly vitalType?: string | null;
  /**
   * The frozen snapshot's own `required`: could the client have sent the form without answering
   * this? It is a property of the question AS IT WAS ASKED, which is why it is read off the
   * occurrence's snapshot rather than off today's template — a question made optional last month
   * was still required on the week the client answered it.
   */
  readonly required: boolean;
}

/** The composite body measurement, field for field as the contract sends it. */
export interface SurveyMeasurementsSource {
  readonly weightKg: number | null;
  readonly bodyFatPercentage: number | null;
  readonly neckCm: number | null;
  readonly shoulderCm: number | null;
  readonly chestCm: number | null;
  readonly bicepCm: number | null;
  readonly forearmCm: number | null;
  readonly waistCm: number | null;
  readonly hipCm: number | null;
  readonly thighCm: number | null;
  readonly calfCm: number | null;
}

export interface SurveyPhotoSource {
  readonly pose: SurveyPhotoPose;
  /** The signed URL, already joined from `photoUrls` by media id. `null` when it was not published. */
  readonly url: string | null;
}

export interface SurveyAnswerSource {
  readonly key: string;
  readonly number: number | null;
  readonly text: string | null;
  readonly choices: readonly string[];
  readonly measurements: SurveyMeasurementsSource | null;
  readonly photos: readonly SurveyPhotoSource[];
  readonly clipId: number | null;
  /**
   * A `GoalBuilder` answer: the goal the client set. Its `text` is empty, so a row reading `text`
   * alone printed „—" for every goal. Absent on every other kind of answer.
   */
  readonly goal?: SurveyGoalSource | null;
}

/** The wire's `PostGoalRequest`, narrowed — the same body the client's goal is created from. */
export interface SurveyGoalSource {
  /** `WeightTarget` | `PrBased` | `SessionCount` | `Custom`; anything else reads as `Custom`. */
  readonly type: string;
  readonly targetValue: number | null;
  /** The starting point the client typed — a weight goal's „Obecna waga". */
  readonly currentValue: number | null;
  readonly deadline: string | null;
  readonly title: string | null;
  readonly description: string | null;
  /** A `PrBased` goal's exercise. The NAME is resolved separately — see `deps.exerciseNames`. */
  readonly exerciseId: number | null;
  /** A `SessionCount` goal's window, in days. */
  readonly periodDays: number | null;
}

export interface SurveyClipSource {
  readonly status: 'Available' | 'Expired';
  readonly videoUrl: string | null;
  readonly posterUrl: string | null;
  readonly durationSeconds: number | null;
  /** When the RECORDING ages out — thirty days, not the twenty-minute signature on its URL. */
  readonly expiresAt: string | null;
}

export interface SurveyOccurrenceSource {
  readonly id: number;
  readonly templateId: number | null;
  readonly title: string;
  /**
   * `Intake` | `CheckIn` | `Reassessment` | `LeadForm`, or `null`. A one-off form (`isOneOffKind`) is
   * not a column — see `buildSurveyHistory`.
   */
  readonly kind: string | null;
  readonly frequency: SurveyCadenceFrequency | null;
  /** `YYYY-MM-DD`. The occurrence this assignment stands for. */
  readonly cycleDate: string | null;
  readonly sentAt: string | null;
  readonly completedAt: string | null;
  readonly status: string | null;
  readonly occurrenceStatus: SurveyOccurrenceStatus | null;
  readonly reviewedAt: string | null;
  readonly questions: readonly SurveyQuestionSource[];
  readonly answers: readonly SurveyAnswerSource[];
  readonly clips: ReadonlyMap<number, SurveyClipSource>;
  /** When this assignment's signed photo URLs stop working. `null` when it published none. */
  readonly photoUrlsExpireAt: string | null;
}

export interface SurveyHistorySource {
  /** Ascending by `cycleDate` — the wire's own order is `SentAt` descending. */
  readonly occurrences: readonly SurveyOccurrenceSource[];
  /** The 200-row cap was hit and the oldest occurrences are not in this answer. */
  readonly isTruncated: boolean;
}

/* ── The ten girths and what a fall in each one MEANS ───────────────────────────────────────── */

export interface MeasurementField {
  readonly key: keyof SurveyMeasurementsSource;
  readonly unit: 'cm' | '%';
  /**
   * Whether a smaller number is the good news.
   *
   * The table is the canon's and the phone's, not a developer's opinion: waist, hips, body fat and
   * neck fall when a client is leaning out; arm, chest, thigh, calf, forearm and shoulder rise when
   * they are gaining. It is the only reason this row may spend an ink at all — a change with no
   * agreed direction is printed in neutral.
   */
  readonly lowGood: boolean;
}

/**
 * The ten sub-rows of „Pełny pomiar", in the canon's own order.
 *
 * WEIGHT is deliberately not here. It is the form's own `checkin_weight` question and gets its own
 * row above the group, which is what the canon draws; a form that carries the composite and no
 * weight question gets it back as the group's first sub-row rather than losing the reading — see
 * `measurementRows`.
 */
export const MEASUREMENT_FIELDS: readonly MeasurementField[] = [
  { key: 'waistCm', unit: 'cm', lowGood: true },
  { key: 'hipCm', unit: 'cm', lowGood: true },
  { key: 'bodyFatPercentage', unit: '%', lowGood: true },
  { key: 'bicepCm', unit: 'cm', lowGood: false },
  { key: 'chestCm', unit: 'cm', lowGood: false },
  { key: 'thighCm', unit: 'cm', lowGood: false },
  { key: 'calfCm', unit: 'cm', lowGood: false },
  { key: 'neckCm', unit: 'cm', lowGood: true },
  { key: 'forearmCm', unit: 'cm', lowGood: false },
  { key: 'shoulderCm', unit: 'cm', lowGood: false },
];

/* ── The view ───────────────────────────────────────────────────────────────────────────────── */

export interface SurveyFormView {
  /** The survey template. An intake with no template stands alone under its own occurrence id. */
  readonly id: number;
  readonly title: string;
}

export interface SurveyColumnView {
  /** The survey assignment id. The key everything else on this tab is addressed by. */
  readonly id: number;
  readonly ordinal: number;
  /**
   * How this occurrence is NAMED in a sentence: „tydz. 12" where the form has a series to be a
   * position in, and its own date where it has none — an intake is asked once, and „tydz. 1" on it
   * would claim a cadence it does not have. See `ordinalOf`.
   */
  readonly ordinalLabel: string;
  /** „14 wrz". */
  readonly dateLabel: string;
  /** „7 paź" — the day the client filled it in, or `null` while it is unanswered. */
  readonly filledLabel: string | null;
  /**
   * How many whole days after its cycle day a LATE occurrence was filled in; `null` for one that
   * was not late, or whose two stamps do not say. The pane words it „2 dni po terminie".
   */
  readonly lateDays: number | null;
  /** The occurrence's outcome: „na czas", „po terminie", „pominięta", „czeka". */
  readonly status: SurveyOccurrenceOutcome;
  readonly statusLabel: string;
  readonly answered: boolean;
  readonly reviewed: boolean;
}

export type SurveyCellState = 'absent' | 'blank' | 'value';

export type SurveyDeltaTone = 'good' | 'warn' | 'flat';

export interface SurveyPhotoView {
  readonly pose: SurveyPhotoPose;
  readonly url: string;
  /** „Przód" — the pose, which is what an `alt` on a progress photo can honestly say. */
  readonly alt: string;
}

export interface SurveyClipView {
  readonly expired: boolean;
  readonly videoUrl: string | null;
  readonly posterUrl: string | null;
  /** „0:38". `null` when the clip carried no duration. */
  readonly durationLabel: string | null;
  /** „wygasa 14 paź" — the recording's own expiry. `null` when the wire did not carry one. */
  readonly expiryLabel: string | null;
}

/**
 * A goal, as the report draws it: the facts that define it as tiles — the full measurement's shape —
 * and the client's own words under them. The matrix and the comparison print `text` instead, a line
 * built from the same facts, because a column has no room for a grid.
 */
export interface SurveyGoalView {
  readonly tiles: readonly SurveyGoalTileView[];
  readonly description: string | null;
}

export interface SurveyGoalTileView {
  readonly key: string;
  /** The goal builder's own field name — „Typ", „Obecna waga", „Cel", „Termin". */
  readonly label: string;
  readonly value: string;
  readonly unit: string | null;
}

export interface SurveyCellView {
  readonly columnId: number;
  readonly state: SurveyCellState;
  /** The raw reading, for a chart or a comparison. `null` on every non-numeric row. */
  readonly number: number | null;
  /** The reading, localised — „74,6". */
  readonly numberText: string | null;
  readonly unit: string | null;
  /** How many of five dashes a `Scale` answer fills. `null` on every other row. */
  readonly scaleFilled: number | null;
  /** A text answer, a choice list joined, or the group row's own word („komplet"). */
  readonly text: string | null;
  readonly bool: boolean | null;
  /** A goal answer, laid out as the report's tiles. Absent on every other kind of answer. */
  readonly goal?: SurveyGoalView | null;
  /**
   * A choice answer's picked options, one chip each (`model/surveyChoiceList`). `null` or absent
   * wherever the answer reads as plain `text`;
   * `text` is filled either way, because the „poprzednio" plate and the accessible reading use it.
   */
  readonly choice?: SurveyChoiceListView | null;
  readonly photos: readonly SurveyPhotoView[];
  readonly clip: SurveyClipView | null;
  /**
   * What the previous ANSWERED occurrence said, in words, on a row that reads as words.
   *
   * The canon's report draws it as a plate under the current answer („POPRZEDNIO · TYDZ. 11"), and
   * it is the only comparison a free-text or a yes/no row can honestly make — there is no number to
   * subtract. `null` on a numeric row, which has a delta instead, and on the first answer of any
   * row, which has nothing behind it.
   */
  readonly previousText: string | null;
  /** The ordinal of the occurrence `previousText` came from — „tydz. 11". `null` with it. */
  readonly previousAtLabel: string | null;
  /** Signed change against the previous ANSWERED occurrence. `null` when there was none. */
  readonly delta: number | null;
  readonly deltaText: string | null;
  readonly deltaTone: SurveyDeltaTone | null;
}

export type SurveyRowKind =
  | 'number'
  | 'scale'
  | 'boolean'
  | 'text'
  | 'goal'
  | 'choice'
  | 'date'
  | 'measurementGroup'
  | 'measurement'
  | 'photos'
  | 'clip'
  | 'other';

export interface SurveyRowView {
  readonly key: string;
  /**
   * The STANDARD catalog code behind this row, or `null` for a question the trainer wrote.
   *
   * It is what the chart strip's INK is keyed to — see `SurveySeriesHue` — because the owner's
   * colour rule is about the quantity, not about the position: blue is sleep wherever it stands.
   */
  readonly code: string | null;
  /** The question as it is ASKED — „Ile dziś ważysz?". The matrix, the report and the comparison. */
  readonly label: string;
  /**
   * The same question in one or two words — „Waga". What the chart strip's eyebrow prints, because
   * a 420px card head cannot carry a sentence and the plot under it is what names the shape.
   *
   * Equal to `label` wherever the catalog offers no short form, which is every question a trainer
   * wrote themselves.
   */
  readonly shortLabel: string;
  /** The second line under a group row's label — „10 wartości · od tydz. 9". */
  readonly caption: string | null;
  readonly kind: SurveyRowKind;
  /** A sub-row of „Pełny pomiar". Indented, and the only row the matrix prints a delta on. */
  readonly sub: boolean;
  /** What every reading on this row is measured in — „cm", „kg". `null` on a bare number. */
  readonly unit: string | null;
  /**
   * Whether a smaller number is the good news on this row, from `MEASUREMENT_FIELDS`.
   *
   * `null` on every row whose direction the DOMAIN does not agree on — a weight, a rating, a
   * trainer's own question — and a change there is printed and never judged. The comparison dialog
   * reads it off the row rather than parsing the field back out of the key.
   */
  readonly lowGood: boolean | null;
  /**
   * The two answers a single-choice question offers, in the order the form lists them, or `null`
   * for every other row.
   *
   * It is what makes the row chartable as a RING, and it is deliberately not the same question as
   * `kind === 'boolean'`: „Tak"/„Nie" is only the catalog's own pair, and „zielony"/„niebieski" is
   * just as drawable and just as unjudgeable. A single-choice question with three options carries
   * `null` — a ring over a share needs exactly two shares.
   */
  readonly binaryOptions: readonly [string, string] | null;
  /**
   * The client could not have sent the form without answering this — read off the FROZEN snapshot
   * this occurrence carries, not off today's template.
   *
   * Every surface that draws a question's label draws the lock beside it (owner, 17 September
   * 2026), so the fact is decided once here rather than by each of them asking the wire. A value of
   * the full measurement is not a question of its own and is never required on its own account —
   * its key is composite, so the lookup misses and it comes back `false`.
   */
  readonly required: boolean;
  readonly cells: readonly SurveyCellView[];
}

/**
 * What a charted series MEASURES, which decides its head, its axis and the shape it is drawn as.
 *
 * `number` — a measured quantity. Head is the newest reading; the axis is fitted to the readings.
 * `scale`  — a 1–5 rating. Head is the window's average; the axis is the rating's own.
 * `binary` — a yes/no question. Not a line at all: a ring over the share of „tak", with one cell
 *            per occurrence beside it. Owner, 16 September 2026, shown the line first — a plot that
 *            only ever sits on two heights is a sawtooth, and a sawtooth reads as volatility.
 *
 * One discriminator rather than a flag per shape: the three differ in every one of those decisions
 * and a reader that tests two booleans has to know which combinations are impossible.
 */
export type SurveySeriesKind = 'number' | 'scale' | 'binary';

/**
 * Which of the three MEANINGS a series' ink carries, or none.
 *
 * Owner rule, 16 September 2026: blue is sleep, ember is energy, lime is an exercise result, and
 * everything else is neutral white ink. It replaced a palette that cycled four tokens by POSITION,
 * which made the same question blue on one form and green on the next and claimed a correspondence
 * that does not exist. Lime is not in this type because no series on this tab is a lift result.
 *
 * The model names the meaning and the component picks the token: which CSS custom property carries
 * „the warm blue" is a fact about the design system, and `model/` may not import one.
 */
export type SurveySeriesHue = 'sleep' | 'energy' | 'neutral';

/** What every charted series prints above its drawing, whichever drawing that is. */
interface SurveySeriesHead {
  readonly key: string;
  /** The question's SHORT name — the card's eyebrow. See `SurveyRowView.shortLabel`. */
  readonly name: string;
  readonly hue: SurveySeriesHue;
  /**
   * The figure the card's head prints, localised. `null` when nothing in range was answered.
   *
   * A measured quantity prints its NEWEST reading — "she weighs 68,4 kg" is a fact about today. A
   * 1–5 rating prints the AVERAGE across the window instead, which is the canon's own head
   * („3,6 /5 śr."): the latest rating is one person's answer on one morning, and a strip that reads
   * "5" beside a plot sitting at three for a month names the outlier as the state of things. A
   * yes/no prints the share of „tak", for the same reason.
   */
  readonly figureText: string | null;
  /** Newest minus oldest across the visible span, rounded as it is printed. */
  readonly delta: number | null;
  /** The same change, localised and signed. */
  readonly deltaText: string | null;
  /** The verdict that change wears, where the quantity has an agreed direction. See `seriesOf`. */
  readonly deltaTone: SurveyDeltaTone | null;
  readonly empty: boolean;
}

/** A quantity drawn as a line. */
export interface SurveyPlotView extends SurveySeriesHead {
  readonly kind: 'number' | 'scale';
  readonly unit: string | null;
  /**
   * The occurrences that were ANSWERED, in order. Never a hole — see `buildSeries`: on this tab the
   * line runs between the readings and only the dots say which occurrences carried one.
   */
  readonly points: readonly ChartPoint[];
}

/** One occurrence on a choice strip. Three states, three DRAWINGS — never three hues of one. */
export interface SurveyChoiceCell {
  /** `a` and `b` are the question's own two options, in the order the form lists them. */
  readonly state: 'a' | 'b' | 'none';
  /** „27 lip · Tak" — the cell's whole meaning, for its tooltip and its accessible name. */
  readonly title: string;
}

/**
 * A two-option question drawn as a ring over a strip of occurrences.
 *
 * ── It is a SHARE, not a verdict ─────────────────────────────────────────────────────────────
 * Owner, 16 September 2026: a two-option single choice is not necessarily yes/no — „zielony" and
 * „niebieski" are just as drawable — so nothing here knows which option is the good news, and the
 * component paints both in neutral ink. The first option is simply the one the SHARE is about,
 * because the form listed it first.
 */
export interface SurveyRingView extends SurveySeriesHead {
  readonly kind: 'binary';
  /** The question's two options, in the form's own order. */
  readonly optionA: string;
  readonly optionB: string;
  /** How many occurrences answered each one. Their sum is the denominator of `percent`. */
  readonly aCount: number;
  readonly bCount: number;
  /** The share of `optionA` among the ANSWERED occurrences, 0–100. */
  readonly percent: number;
  /** „5/7" — what the ring's hole prints, because a share alone has no denominator. */
  readonly countText: string;
  /** Every occurrence in range, answered or not: the strip is about the window, not the answers. */
  readonly cells: readonly SurveyChoiceCell[];
  /** The first and last column's dates, printed under the strip so it can be read as a period. */
  readonly fromLabel: string;
  readonly toLabel: string;
}

export type SurveySeriesView = SurveyPlotView | SurveyRingView;

export interface SurveyHistoryView {
  /** The form the pane's occurrence belongs to. The chips are `model/surveyStream`'s. */
  readonly activeForm: SurveyFormView | null;
  /** EVERY occurrence of that form, oldest first. Never narrowed by the range — see the header. */
  readonly columns: readonly SurveyColumnView[];
  /** The occurrence the pane is showing, resolved. `null` only when there is nothing to show. */
  readonly selected: SurveyColumnView | null;
  readonly rows: readonly SurveyRowView[];
  readonly series: readonly SurveySeriesView[];
  /** The span every chart is drawn over, so one date lands on one pixel column in all of them. */
  readonly xDomain: readonly [Date, Date] | null;
  readonly isTruncated: boolean;
  /** No occurrence at all — the client has never been sent a survey. */
  readonly empty: boolean;
  /**
   * The earliest expiry among the visible occurrences' signed photo URLs. A dialog opening after it
   * has passed re-reads the list rather than drawing a broken image.
   */
  readonly photoUrlsExpireAt: string | null;
}

export interface SurveyHistoryDeps {
  readonly t: Translate;
  readonly locale: string;
  /**
   * `YYYY-MM-DD` bounds, inclusive, applied to `cycleDate` — and ONLY to what is charted.
   *
   * The columns, the rows and the cells are the form's whole history: a stream row outside the
   * range still has a report, and a comparison between two of them is still arithmetic.
   */
  readonly chartRange: { readonly from: string; readonly to: string };
  /**
   * The occurrence the pane is showing, which is also what picks the form.
   *
   * `null` — nothing chosen yet — resolves to the newest ANSWERED occurrence of any form, the
   * same rule `model/surveyStream` opens the stream on. One rule, stated once.
   */
  readonly occurrenceId: number | null;
  /** `exerciseId` → name, for an `ExerciseClip` question whose label is the trainer's free text. */
  readonly exerciseNames: ReadonlyMap<number, string>;
}

/* ── Building it ────────────────────────────────────────────────────────────────────────────── */

/** The canon's rating control is five dashes, and the catalog's `Scale` is 1–5. */
export const SCALE_STEPS = 5;

/** Steps between two `YYYY-MM-DD` cycle dates at the form's cadence. Days, weeks or months. */
export function cadenceSteps(
  from: string,
  to: string,
  frequency: SurveyCadenceFrequency | null,
): number {
  const start = parseDateOnly(from);
  const end = parseDateOnly(to);
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  if (frequency === 'Month') {
    const a = new Date(start);
    const b = new Date(end);
    return (
      (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth())
    );
  }
  const days = Math.round((end - start) / MS_PER_DAY);
  // `Day` and an unknown cadence both step by the day: a cadence this build cannot name still has
  // occurrences in a real order, and counting them by day is the reading that cannot be wrong by
  // more than a label.
  return frequency === 'Week' ? Math.round(days / 7) : days;
}

/** The noun the ordinal is counted in — „tydz." / „dz." / „mies." */
const ORDINAL_KEYS: Readonly<Record<SurveyCadenceFrequency, string>> = {
  Day: 'web:clientDetail.surveys.ordinal.day',
  Week: 'web:clientDetail.surveys.ordinal.week',
  Month: 'web:clientDetail.surveys.ordinal.month',
};

/** A cadence this build cannot name is counted by the week — the form the canon draws. */
export function ordinalKey(frequency: SurveyCadenceFrequency | null): string {
  return frequency === null ? ORDINAL_KEYS.Week : ORDINAL_KEYS[frequency];
}

/**
 * Where an occurrence stands in its form's series, and the words for it — „tydz. 12".
 *
 * ONE implementation, because two surfaces print it: the pane's column heads and the stream's rows.
 * They carried a copy each and the copies had already parted — a form with a single occurrence was
 * „tydz. 1" in the pane and no position at all in the stream, which is one occurrence described two
 * ways on one screen.
 *
 * `null` where there is no series to be a position in: a group of one — an intake always is — a
 * group whose earliest occurrence carries no cycle date, or an occurrence carrying none itself.
 * What to print instead is the CALLER's: the stream prints the date alone, a column names itself by
 * its date.
 */
export function ordinalOf(
  group: readonly SurveyOccurrenceSource[],
  occurrence: SurveyOccurrenceSource,
  t: Translate,
): { readonly ordinal: number; readonly label: string } | null {
  const anchor = group[0]?.cycleDate ?? null;
  const cycleDate = occurrence.cycleDate;
  if (group.length < 2 || anchor === null || cycleDate === null) return null;
  // The cadence the form is sent on TODAY — its newest occurrence — because a form whose cadence
  // changed counts its whole series in the noun it is sent in now.
  const frequency = group.at(-1)?.frequency ?? null;
  const ordinal = cadenceSteps(anchor, cycleDate, frequency) + 1;
  return { ordinal, label: t(ordinalKey(frequency), { n: ordinal }) };
}

/**
 * A goal type, as the goal builder's own key (`clients.goalBuilder.types.<key>.label`) — the words the
 * client chose it by. A type this build does not know reads as the builder's „Własny".
 */
const GOAL_TYPE_KEYS: Readonly<Record<string, 'weight' | 'pr' | 'sessions' | 'custom'>> = {
  WeightTarget: 'weight',
  PrBased: 'pr',
  SessionCount: 'sessions',
  Custom: 'custom',
};

/** The four units a reading on this tab is measured in, and the key each one's word lives under. */
const UNIT_KEYS: Readonly<Record<'cm' | '%' | 'kg' | 'h', string>> = {
  kg: 'clients.detail.body.unitKg',
  '%': 'clients.detail.body.unitPct',
  cm: 'web:clientDetail.body.unitCm',
  h: 'clients.detail.progress.sleep.hour',
};

/**
 * What a question that reads as WORDS is, by its answer type. Everything absent from here is `text`
 * — the honest rendering of an answer this app has no drawing for is the answer — and a type this
 * build cannot name is `other`.
 */
const WORD_ROW_KINDS: Readonly<Partial<Record<SurveyAnswerType, SurveyRowKind>>> = {
  SingleChoice: 'choice',
  MultiChoice: 'choice',
  Date: 'date',
  GoalBuilder: 'goal',
};

/** The one number formatter every figure on this tab is printed through. */
function readingFormat(locale: string): Intl.NumberFormat {
  return numberFormat(locale, { maximumFractionDigits: 1 });
}

/**
 * A change, as the trainer READS it — rounded to the one decimal every figure on this tab is
 * printed at.
 *
 * Both the words and the verdict are taken from this rather than from the raw subtraction. Two
 * girths a twentieth of a centimetre apart differ by 0.049999999 in binary, which prints as „0" and
 * would have been painted green: a win invented by floating point. Rounding first means the sign,
 * the colour and the number always agree.
 */
export function roundedDelta(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * „+0,4" / „−1,4" / „0", localised.
 *
 * The formatter comes from `shared/lib/intlCache` rather than being constructed here: this is called
 * once per CELL, and building an `Intl.NumberFormat` is thirty times the cost of using one.
 */
export function signed(value: number, locale: string): string {
  const rounded = roundedDelta(value);
  const magnitude = readingFormat(locale).format(Math.abs(rounded));
  // A true minus sign, not a hyphen — the same glyph `model/measurements` prints.
  return rounded > 0 ? `+${magnitude}` : rounded < 0 ? `−${magnitude}` : magnitude;
}

/**
 * „−0,6 kg" — a signed change carrying what it is measured in, or the bare figure where there is no
 * unit to carry.
 *
 * One spelling, because two surfaces print it: the comparison's change column and the report's own
 * change line. Both had the same conditional written out.
 *
 * ── A change of ZERO is not „0 kg" ───────────────────────────────────────────────────────────
 * Owner, 16 September 2026. „0" beside a weight is a figure the trainer has to read twice to learn
 * that nothing happened, and it wears the same shape as a reading. The sentence says it instead,
 * and the arrow beside it is already absent — there is no direction to point in. The DECISION is
 * here rather than in the two components that draw it, so the two cannot part.
 */
export function changeWithUnit(
  t: Translate,
  change: number,
  text: string,
  unit: string | null,
): string {
  if (change === 0) return t('web:clientDetail.surveys.unchanged');
  return unit === null ? text : t('web:clientDetail.surveys.changeUnit', { value: text, unit });
}

/** „0:38". Seconds are the contract's unit. */
function durationLabel(seconds: number | null): string | null {
  return seconds == null || seconds < 0 ? null : clockDuration(seconds);
}

/** An empty cell of the given state. Every builder below starts from one and fills what it has. */
function emptyCell(columnId: number, state: SurveyCellState): SurveyCellView {
  return {
    columnId,
    state,
    number: null,
    numberText: null,
    unit: null,
    scaleFilled: null,
    text: null,
    bool: null,
    photos: [],
    clip: null,
    previousText: null,
    previousAtLabel: null,
    delta: null,
    deltaText: null,
    deltaTone: null,
  };
}

/**
 * The tone a signed change wears on a row whose direction is agreed.
 *
 * `flat` for no change — the figure is still worth printing, and painting a zero green would claim
 * a win the client did not have. `lowGood: null` never reaches here; the caller passes the field's
 * own table value or does not call at all.
 *
 * Exported with `signed` above it because the comparison dialog reaches the same two verdicts about
 * the same ten fields (`model/surveyCompare`). Two copies of "which way is good" would have been two
 * places for the table to drift, and the whole point of `MEASUREMENT_FIELDS` is that there is one.
 */
export function deltaTone(delta: number, lowGood: boolean): SurveyDeltaTone {
  if (delta === 0) return 'flat';
  return delta < 0 === lowGood ? 'good' : 'warn';
}

/* ── The three passes, and the one context they share ───────────────────────────────────────── */

/**
 * Everything the rows and the series are decided against, settled once.
 *
 * The columns come first because every later pass is indexed BY them: a cell belongs to a column, a
 * chart point is placed at a column's cycle date, and the „pytanie dodane w tydz. 9" plate is a
 * column index. So `buildColumns` runs, its answer joins this object, and `buildRows` and
 * `buildSeries` read it — three passes over one decided state rather than one 480-line body whose
 * locals are its interface.
 */
interface SurveyBuildContext {
  readonly t: Translate;
  readonly locale: string;
  /** Every figure on this tab, at one decimal. */
  readonly number: Intl.NumberFormat;
  /** „14 wrz". Also the clip's own expiry date. */
  readonly dayMonth: Intl.DateTimeFormat;
  readonly exerciseNames: ReadonlyMap<number, string>;
  /** The occurrences drawn, oldest first. Index for index with `columns`. */
  readonly visible: readonly SurveyOccurrenceSource[];
  readonly columns: readonly SurveyColumnView[];
  /**
   * Which of `visible` the CHARTS are drawn over — the range bar's window, ascending.
   *
   * The rows and the cells are built over every occurrence, because a report and a comparison are
   * not questions about a window. Only `buildSeries` reads this.
   */
  readonly chartIndices: readonly number[];
  /** Per visible occurrence: its answers by key. */
  readonly answersOf: readonly ReadonlyMap<string, SurveyAnswerSource>[];
  /** Per visible occurrence: the questions its snapshot ASKED, by key. */
  readonly asksOf: readonly ReadonlyMap<string, SurveyQuestionSource>[];
}

/**
 * The occurrence heads.
 *
 * The ordinal is `ordinalOf`'s — counted against the earliest occurrence of the FORM in the fetched
 * list, stepped by the form's own cadence. It is an ordinal IN VIEW: the read is capped at 200 with
 * no `from`/`to`, so on a long history that earliest occurrence is not enrolment.
 */
function buildColumns(
  visible: readonly SurveyOccurrenceSource[],
  deps: { readonly t: Translate; readonly locale: string },
): SurveyColumnView[] {
  const { t, locale } = deps;
  const dayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
  const filledFormat = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });

  return visible.map((occurrence) => {
    const cycleDate = occurrence.cycleDate ?? '';
    const position = ordinalOf(visible, occurrence, t);
    const dateLabel = dayMonth.format(new Date(`${cycleDate}T00:00:00Z`));
    const answered = occurrence.status === 'Completed';
    // `visible` is the form's own occurrences, oldest first: the last is its newest.
    const status = occurrenceOutcomeOf(occurrence, occurrence === visible.at(-1));
    const completed = occurrence.completedAt === null ? null : new Date(occurrence.completedAt);
    const filled = completed === null || Number.isNaN(completed.getTime()) ? null : completed;
    const lateBy = status === 'Late' && filled !== null ? daysLate(cycleDate, filled) : null;
    return {
      id: occurrence.id,
      ordinal: position?.ordinal ?? 1,
      ordinalLabel: position?.label ?? dateLabel,
      dateLabel,
      filledLabel: answered && filled !== null ? filledFormat.format(filled) : null,
      lateDays: lateBy,
      status,
      statusLabel: t(`web:clientDetail.surveys.status.${status}`),
      answered,
      reviewed: occurrence.reviewedAt !== null,
    };
  });
}

/*
 * What a question is CALLED, long and short: `shared/lib/standardQuestionNames`, promoted out of
 * this file on 17 September 2026 when `pages/reports` became its second consumer. The reading it
 * carries — a coded question is named from its code, a trainer's own keeps its wording, and a
 * missing key falls back to the wire rather than to the key — is unchanged, and so is this tab's:
 * `named.label` is the question as asked with its phone line break collapsed to a space, because a
 * matrix row is one line in a 250px column.
 */

interface NumericRowOptions {
  /** The word the figure is measured in, printed beside it. `null` on a bare number. */
  readonly unit: string | null;
  /**
   * Whether a smaller number is the good news. `null` for a quantity with no agreed direction — a
   * weight, a trainer's own question — and the delta is then printed in neutral rather than judged.
   */
  readonly lowGood: boolean | null;
  /** Also fill the five dashes of the rating control. A `Scale` answer, and nothing else. */
  readonly scale: boolean;
}

/**
 * Every question of the active form, as a row of cells.
 *
 * One pass, because the row order, the „added at" plate and the delta chain all depend on the same
 * walk: a cell's delta is against the previous cell of ITS OWN row that carried a reading, which is
 * only knowable by going along the row once.
 */
function buildRows(context: SurveyBuildContext): SurveyRowView[] {
  const { t, locale, number, dayMonth, exerciseNames, visible, columns, answersOf, asksOf } =
    context;

  /* ── The question order: today's form first, retired questions after it ─────────────────── */
  const ordered: SurveyQuestionSource[] = [];
  const seen = new Set<string>();
  for (let index = visible.length - 1; index >= 0; index -= 1) {
    for (const question of visible[index]?.questions ?? []) {
      if (seen.has(question.key)) continue;
      seen.add(question.key);
      ordered.push(question);
    }
  }

  /** The first visible column whose snapshot asks this question. `-1` when none does. */
  const firstAskedAt = (key: string): number => asksOf.findIndex((asks) => asks.has(key));

  const unitOf = (unit: 'cm' | '%' | 'kg' | 'h'): string => t(UNIT_KEYS[unit]);

  /**
   * Weight is the form's own question wherever the form asks one. A form that carries the composite
   * and NO weight question would otherwise lose the reading entirely, so it comes back as the
   * group's first sub-row — the number is the point, not which envelope it arrived in. Same rule
   * `toLatestSurvey` follows for the Przegląd tile. Asked once, of the whole form.
   */
  const asksWeight = ordered.some(
    (question) => question.answerType === 'Number' && WEIGHT_KEYS.includes(question.key),
  );

  /** One numeric row, cell by cell, with a delta against the previous cell that carried a reading. */
  function numericCells(
    key: string,
    read: (index: number) => number | null,
    options: NumericRowOptions,
  ): SurveyCellView[] {
    let previous: number | null = null;
    return columns.map((column, index) => {
      if (!(asksOf[index]?.has(key) ?? false)) return emptyCell(column.id, 'absent');
      const value = read(index);
      if (value === null) return emptyCell(column.id, 'blank');
      const delta = previous === null ? null : value - previous;
      previous = value;
      return {
        ...emptyCell(column.id, 'value'),
        number: value,
        numberText: number.format(value),
        unit: options.unit,
        scaleFilled: options.scale
          ? Math.max(0, Math.min(SCALE_STEPS, Math.round(value)))
          : null,
        /* ROUNDED on the cell, not only where it is printed. The arrow, the verdict, the figure
         * and „bez zmian" are all read off `delta`, and two girths a twentieth of a centimetre
         * apart differ by 0.049999999 in binary: the tile drew a DOWN arrow beside „0 cm"
         * because the words were rounded and the sign was not. See `roundedDelta`. */
        delta: delta === null ? null : roundedDelta(delta),
        deltaText: delta === null ? null : signed(delta, locale),
        deltaTone:
          delta === null
            ? null
            : options.lowGood === null
              ? 'flat'
              : deltaTone(roundedDelta(delta), options.lowGood),
      };
    });
  }

  const deadlineFormat = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

  /**
   * A goal as tiles, each named by the goal builder's own field — the words the client filled it in
   * by. Only the facts the goal carries get a tile: a weight goal set without a starting weight has
   * no „Obecna waga".
   */
  function goalView(goal: SurveyGoalSource): SurveyGoalView {
    const typeKey = GOAL_TYPE_KEYS[goal.type] ?? 'custom';
    const tiles: SurveyGoalTileView[] = [
      {
        key: 'type',
        label: t('clients.goalBuilder.saved.recap.type'),
        value: t(`clients.goalBuilder.types.${typeKey}.label`),
        unit: null,
      },
    ];
    const add = (key: string, labelKey: string, value: string | null, unit: string | null = null) => {
      if (value !== null && value !== '') tiles.push({ key, label: t(labelKey), value, unit });
    };
    const figure = (value: number | null) => (value === null ? null : number.format(value));
    if (typeKey === 'weight') {
      add('current', 'clients.goalBuilder.fields.currentWeight', figure(goal.currentValue), unitOf('kg'));
      add('target', 'clients.goalBuilder.fields.target', figure(goal.targetValue), unitOf('kg'));
    } else if (typeKey === 'pr') {
      const exercise = goal.exerciseId === null ? null : (exerciseNames.get(goal.exerciseId) ?? null);
      add('exercise', 'clients.goalBuilder.fields.exercise', exercise);
      add('target', 'clients.goalBuilder.fields.target', figure(goal.targetValue), unitOf('kg'));
    } else if (typeKey === 'sessions') {
      add('sessions', 'clients.goalBuilder.fields.sessionCount', figure(goal.targetValue));
      add(
        'period',
        'clients.goalBuilder.fields.period',
        figure(goal.periodDays),
        t('clients.goalBuilder.fields.units.days'),
      );
    } else {
      add('title', 'clients.goalBuilder.fields.title', goal.title?.trim() ?? null);
    }
    const deadline = goal.deadline === null ? Number.NaN : parseDateOnly(goal.deadline.slice(0, 10));
    add(
      'deadline',
      'clients.goalBuilder.fields.deadline',
      Number.isNaN(deadline) ? null : deadlineFormat.format(deadline),
    );
    const description = goal.description?.trim() ?? '';
    return { tiles, description: description === '' ? null : description };
  }

  /**
   * A goal, as one line: its type, what it aims at, and by when — „Waga ciała · 90 → 82 kg · do
   * 15 lis 2026", the description after a dash. The type names are the goal builder's own, the ones
   * the client picked from, so the trainer reads the goal in the words it was set in.
   */
  function goalWords(goal: SurveyGoalSource): string {
    const typeKey = GOAL_TYPE_KEYS[goal.type] ?? 'custom';
    const parts = [t(`clients.goalBuilder.types.${typeKey}.label`)];
    const target = goal.targetValue === null ? null : number.format(goal.targetValue);
    if (typeKey === 'weight' && target !== null) {
      const start = goal.currentValue === null ? null : number.format(goal.currentValue);
      parts.push(`${start === null ? '' : `${start} → `}${target} ${unitOf('kg')}`);
    } else if (typeKey === 'pr') {
      const exercise = goal.exerciseId === null ? null : (exerciseNames.get(goal.exerciseId) ?? null);
      if (exercise !== null) parts.push(exercise);
      if (target !== null) parts.push(`${target} ${unitOf('kg')}`);
    } else if (typeKey === 'sessions' && target !== null) {
      parts.push(
        goal.periodDays === null
          ? `${target} ${t('intakeSurvey.goalUnit.sessions')}`
          : t('clients.goalBuilder.saved.recap.sessionsValue', {
              sessions: target,
              days: goal.periodDays,
            }),
      );
    }
    const title = goal.title?.trim() ?? '';
    if (typeKey === 'custom' && title !== '') parts.push(title);
    // The wire's `date-time` names a calendar day; its date part is the day the client picked.
    const deadline = goal.deadline === null ? Number.NaN : parseDateOnly(goal.deadline.slice(0, 10));
    if (!Number.isNaN(deadline)) {
      parts.push(`${t('intakeSurvey.until')} ${deadlineFormat.format(deadline)}`);
    }
    const line = parts.join(' · ');
    const description = goal.description?.trim() ?? '';
    return description === '' ? line : `${line} — ${description}`;
  }

  /**
   * One row that reads as WORDS, cell by cell, carrying the previous answered occurrence's own
   * words forward exactly as `numericCells` carries the previous reading.
   *
   * A free-text or a yes/no row has no subtraction to offer, and "what did she say last time" is
   * the comparison the trainer is actually making — the canon's report draws it as a plate under
   * the current answer.
   *
   * A catalog question's choices arrive as enum members or equipment codes and are printed by their
   * Polish names (`standardOptionLabel`); a trainer's own options are already their own words.
   *
   * @param question the asked question: its key, its catalog code (`null` on a trainer's own) and options.
   * @param yes the word that means `true` on a yes/no row, or `null` on a row that is not one.
   * @param isDate the question asks for a date, whose civil `YYYY-MM-DD` is written for the reader.
   */
  function wordCells(
    question: SurveyQuestionSource,
    answerAt: (index: number) => SurveyAnswerSource | null,
    yes: string | null,
    isDate: boolean,
  ): SurveyCellView[] {
    const { key, code } = question;
    let previousWords: string | null = null;
    let previousAt: string | null = null;
    return columns.map((column, index) => {
      if (!(asksOf[index]?.has(key) ?? false)) return emptyCell(column.id, 'absent');
      const answer = answerAt(index);
      // A single choice arrives in `text` rather than `choices` („Male", „Beginner"), so it is named
      // the same way; a code with no option words keeps its text, which is every free-text answer.
      const words =
        answer === null
          ? ''
          : answer.goal != null
            ? goalWords(answer.goal)
            : answer.choices.length > 0
              ? answer.choices.map((choice) => standardOptionLabel(t, code, choice)).join(', ')
              : isDate
                ? // A date answer is a civil date on the wire; it reads as „12.06.1994".
                  writtenCivilDate(answer.text ?? '', locale)
                : standardOptionLabel(t, code, answer.text ?? '');
      if (words.trim() === '') return emptyCell(column.id, 'blank');
      const cell: SurveyCellView = {
        ...emptyCell(column.id, 'value'),
        text: words,
        goal: answer?.goal == null ? null : goalView(answer.goal),
        choice: answer === null ? null : choiceListOf(t, question, answer),
        bool: yes === null ? null : words === yes,
        previousText: previousWords,
        previousAtLabel: previousWords === null ? null : previousAt,
      };
      previousWords = words;
      previousAt = column.ordinalLabel;
      return cell;
    });
  }

  const rows: SurveyRowView[] = [];

  /**
   * A row, with the three members every call site would otherwise have to spell out.
   *
   * The „pytanie dodane w tydz. 9" plate went with the matrix on 16 September 2026: the report draws
   * one occurrence, and a question that occurrence was never asked is simply not in it — the plate
   * existed to stop N empty cells reading as N unanswered weeks, and there are no cells any more.
   * The distinction it stood on is still the model's, as `SurveyCellView.state`.
   */
  /** Which questions the form asked as REQUIRED, by key. A sub-row's composite key simply misses. */
  const requiredOf = new Map(ordered.map((question) => [question.key, question.required]));

  const pushRow = (
    row: Omit<SurveyRowView, 'shortLabel' | 'code' | 'binaryOptions' | 'required'> & {
      /** Omitted on every row that is not a two-option single choice. */
      readonly binaryOptions?: readonly [string, string] | null;
      /** Omitted where the catalog offers no short form — the long name is then the only one. */
      readonly shortLabel?: string;
      /** Omitted on a row that is not a question of its own — a value of the full measurement. */
      readonly code?: string | null;
    },
  ) => {
    rows.push({
      ...row,
      shortLabel: row.shortLabel ?? row.label,
      code: row.code ?? null,
      binaryOptions: row.binaryOptions ?? null,
      required: requiredOf.get(row.key) ?? false,
    });
  };

  for (const question of ordered) {
    const key = question.key;
    const askedAt = firstAskedAt(key);
    const answerAt = (index: number) => answersOf[index]?.get(key) ?? null;
    const named = questionNames(t, question);

    if (question.answerType === 'Measurements') {
      /* ── „Pełny pomiar": the group row, then every one of its values ──────────────────────
       * The group's own cells carry a WORD rather than a number — which of the ten arrived —
       * because a composite answer has no single figure and the ten sub-rows below already print
       * every one it has. */
      let first = true;
      const groupCells = columns.map((column, index) => {
        if (!(asksOf[index]?.has(key) ?? false)) return emptyCell(column.id, 'absent');
        const measurements = answerAt(index)?.measurements ?? null;
        if (measurements === null) return emptyCell(column.id, 'blank');
        const present = MEASUREMENT_FIELDS.filter(
          (field) => measurements[field.key] !== null,
        ).length;
        const wasFirst = first;
        first = false;
        return {
          ...emptyCell(column.id, 'value'),
          text: wasFirst
            ? t('web:clientDetail.surveys.measureFirst')
            : present === MEASUREMENT_FIELDS.length
              ? t('web:clientDetail.surveys.measureComplete')
              : t('web:clientDetail.surveys.measurePartial', {
                  count: present,
                  total: MEASUREMENT_FIELDS.length,
                }),
        };
      });
      pushRow(
        {
          key,
          label: named.label,
          shortLabel: named.shortLabel,
          code: question.code,
          caption:
            askedAt > 0
              ? t('web:clientDetail.surveys.measureCaptionSince', {
                  count: MEASUREMENT_FIELDS.length,
                  at: columns[askedAt]?.ordinalLabel ?? '',
                })
              : t('web:clientDetail.surveys.measureCaption', {
                  count: MEASUREMENT_FIELDS.length,
                }),
          kind: 'measurementGroup',
          sub: false,
          unit: null,
          lowGood: null,
          cells: groupCells,
        },
      );

      const subRow = (
        field: keyof SurveyMeasurementsSource,
        unit: string,
        lowGood: boolean | null,
      ) => {
        pushRow(
          {
            key: `${key}:${field}`,
            label: t(`clients.detail.surveys.measure.${field}`),
            caption: null,
            kind: 'measurement',
            sub: true,
            unit,
            lowGood,
            cells: numericCells(key, (index) => answerAt(index)?.measurements?.[field] ?? null, {
              unit,
              lowGood,
              scale: false,
            }),
          },
        );
      };

      // Weight has no direction this app may claim: a client cutting and a client bulking read the
      // same fall in opposite ways, and only their goal says which. So it is printed and never
      // judged, wherever it stands.
      if (!asksWeight) subRow('weightKg', unitOf('kg'), null);
      for (const field of MEASUREMENT_FIELDS) {
        subRow(field.key, unitOf(field.unit), field.lowGood);
      }
      continue;
    }

    if (question.answerType === 'Number' || question.answerType === 'Scale') {
      // A rating is a number with a control drawn round it, so it is the same row with two things
      // switched on: the five dashes, and a direction. The scale's five is the top of the control,
      // so a rating that ROSE is the good news — that is a fact about the control rather than about
      // the trainer's question, which is why it is not in `lowGood` on the row. A measured quantity
      // has no direction this app may claim and is printed in neutral.
      const scale = question.answerType === 'Scale';
      // The standard quantities carry their unit: a weight in kilograms, the intake's height in
      // centimetres, hours slept in hours (`intake-survey` R37). Any other number is the trainer's
      // own and names none.
      const unit = scale
        ? null
        : WEIGHT_KEYS.includes(key)
          ? unitOf('kg')
          : key === HEIGHT_KEY
            ? unitOf('cm')
            : questionVital(question) === 'SleepDuration'
              ? unitOf('h')
              : null;
      pushRow(
        {
          key,
          label: named.label,
          shortLabel: named.shortLabel,
          code: question.code,
          caption: null,
          kind: scale ? 'scale' : 'number',
          sub: false,
          unit,
          lowGood: null,
          cells: numericCells(key, (index) => answerAt(index)?.number ?? null, {
            unit,
            lowGood: scale ? false : null,
            scale,
          }),
        },
      );
      continue;
    }

    if (question.answerType === 'Photo') {
      pushRow(
        {
          key,
          label: named.label,
          shortLabel: named.shortLabel,
          code: question.code,
          caption: null,
          kind: 'photos',
          sub: false,
          unit: null,
          lowGood: null,
          cells: columns.map((column, index) => {
            if (!(asksOf[index]?.has(key) ?? false)) return emptyCell(column.id, 'absent');
            const photos = (answerAt(index)?.photos ?? []).flatMap((photo) =>
              photo.url === null
                ? []
                : [
                    {
                      pose: photo.pose,
                      url: photo.url,
                      alt: t(`clients.detail.surveys.pose.${photo.pose}`),
                    },
                  ],
            );
            if (photos.length === 0) return emptyCell(column.id, 'blank');
            return { ...emptyCell(column.id, 'value'), photos };
          }),
        },
      );
      continue;
    }

    if (question.answerType === 'ExerciseClip') {
      const exerciseName =
        question.exerciseId === null ? null : (exerciseNames.get(question.exerciseId) ?? null);
      pushRow(
        {
          key,
          // The trainer's own wording is the label; the exercise it is about is appended when the
          // catalogue answered, exactly as the phone does it. Neither replaces the other — UNLESS
          // the trainer already typed the exercise into the label, which is the common case:
          // „Nagranie: przysiad ze sztangą" came back as „Nagranie: przysiad ze sztangą · Przysiad
          // ze sztangą".
          label:
            exerciseName === null ||
            named.label.toLocaleLowerCase(locale).includes(exerciseName.toLocaleLowerCase(locale))
              ? named.label
              : t('web:clientDetail.surveys.clipLabel', {
                  label: named.label,
                  exercise: exerciseName,
                }),
          code: question.code,
          caption: t('web:clientDetail.surveys.clipCaption'),
          kind: 'clip',
          sub: false,
          unit: null,
          lowGood: null,
          cells: columns.map((column, index) => {
            if (!(asksOf[index]?.has(key) ?? false)) return emptyCell(column.id, 'absent');
            const clipId = answerAt(index)?.clipId ?? null;
            const clip = clipId === null ? null : (visible[index]?.clips.get(clipId) ?? null);
            if (clip === null) return emptyCell(column.id, 'blank');
            return {
              ...emptyCell(column.id, 'value'),
              clip: {
                expired: clip.status === 'Expired',
                videoUrl: clip.videoUrl,
                posterUrl: clip.posterUrl,
                durationLabel: durationLabel(clip.durationSeconds),
                expiryLabel:
                  clip.expiresAt === null || clip.status === 'Expired'
                    ? null
                    : t('web:clientDetail.surveys.clipExpiresAt', {
                        date: dayMonth.format(new Date(clip.expiresAt)),
                      }),
              },
            };
          }),
        },
      );
      continue;
    }

    /* ── Everything that reads as words ────────────────────────────────────────────────────
     * A yes/no is a `SingleChoice` over two options in the catalog, and it is what the canon draws
     * as a tick. Anything else prints its own text — a date, a choice list, a goal builder's
     * summary — because the honest rendering of an answer this app has no drawing for is the
     * answer. */
    const binaryYes = t('clients.detail.surveys.binary.yes');
    const binaryNo = t('clients.detail.surveys.binary.no');
    const binary =
      question.answerType === 'SingleChoice' &&
      question.options.length === 2 &&
      question.options.includes(binaryYes) &&
      question.options.includes(binaryNo);

    /* A single choice over exactly two options is a SHARE, whatever the two words are. It is a
     * separate question from `binary` above: that one is the catalog's own Tak/Nie, which the
     * matrix draws as a tick, and this one is „zielony"/„niebieski" as well. */
    const pair: readonly [string, string] | null =
      question.answerType === 'SingleChoice' &&
      question.options.length === 2 &&
      question.options[0] !== undefined &&
      question.options[1] !== undefined
        ? [
            standardOptionLabel(t, question.code, question.options[0]),
            standardOptionLabel(t, question.code, question.options[1]),
          ]
        : null;

    pushRow(
      {
        key,
        label: named.label,
        shortLabel: named.shortLabel,
        code: question.code,
        binaryOptions: pair,
        caption: null,
        kind: binary
          ? 'boolean'
          : question.answerType === null
            ? 'other'
            : (WORD_ROW_KINDS[question.answerType] ?? 'text'),
        sub: false,
        unit: null,
        lowGood: null,
        cells: wordCells(
          question,
          answerAt,
          binary ? binaryYes : null,
          question.answerType === 'Date',
        ),
      },
    );
  }

  return rows;
}

/**
 * The charts over the matrix: what is drawn, and how.
 *
 * ── Only what the form asks TODAY is charted ─────────────────────────────────────────────────
 * Owner, 16 September 2026. A question the trainer removed is still a matrix ROW — the answers were
 * given and deleting the history would be a lie — but it is not a chart: a strip is what the trainer
 * is tracking now, and a card for a retired question is a card nobody will ever add a reading to.
 * „Today" is the newest occurrence that carries a snapshot at all; a MISSED newest occurrence still
 * froze the form when it went out, so this is normally just the last one, and the `find` is what
 * covers a wire that sent an occurrence with no questions on it.
 *
 * ── The LINE never breaks; the DOTS are the readings ─────────────────────────────────────────
 * Same round: a missed occurrence must not cut the line. So a plot carries only the occurrences
 * that were ANSWERED, and the x axis stays real time — a fortnight with one missed week is one
 * segment twice as long as its neighbours, sloping between the two readings that actually exist,
 * with no mark in the middle. The gap is still visible in the only place that cannot be misread:
 * the spacing.
 *
 * This is deliberately the OPPOSITE of `shared/lib/chartSeries`' house rule, which keeps a hole as
 * a `null` so the renderer draws a break, and Trendy still follows it — a week nobody trained is a
 * week of zero tonnage the trainer needs to see as an absence. A check-in is not a measurement of
 * the week; it is a measurement of the client, taken on the days she answered. Nothing is
 * interpolated either way: no point is invented, only the segment between two real ones is drawn.
 *
 * ── A yes/no question is a RING, not a line ──────────────────────────────────────────────────
 * Shown the line first, the owner rejected it: a plot that only ever sits on two heights is a
 * sawtooth, and a sawtooth reads as volatility rather than as „six weeks out of ten". So the card
 * is the attendance card's own language — a ring over the share, with one cell per occurrence
 * beside it in the three drawings `shared/ui/TrainingWeekStrip` and Frekwencja already use.
 */
function buildSeries(
  context: SurveyBuildContext,
  rows: readonly SurveyRowView[],
): SurveySeriesView[] {
  const { t, locale, number, visible, columns, chartIndices } = context;
  /** A share is a whole number of occurrences out of a whole number — „70 %", never „70,0 %". */
  const share = numberFormat(locale, { maximumFractionDigits: 0 });

  /** The window, as occurrences. Every figure below is counted over exactly these. */
  const inWindow = chartIndices.flatMap((index) => {
    const occurrence = visible[index];
    return occurrence === undefined ? [] : [occurrence];
  });

  /**
   * What the form asks TODAY — read off the newest occurrence IN THE WINDOW that carried a
   * snapshot. Empty only when the window holds no snapshot at all, and the strip then draws its
   * one „nothing measurable here" card.
   */
  const current = new Set(
    [...inWindow].reverse().find((occurrence) => occurrence.questions.length > 0)?.questions.map(
      (question) => question.key,
    ) ?? [],
  );
  /** A sub-row of the full measurement is charted on ITS QUESTION's behalf, not its own key. */
  const charted = (row: SurveyRowView): boolean =>
    current.has(row.sub ? (row.key.split(':')[0] ?? row.key) : row.key);

  const dateAt = (index: number): Date => new Date(`${visible[index]?.cycleDate ?? ''}T00:00:00Z`);

  /** A row's cells, narrowed to the window — the one place the range reaches a projection. */
  const windowCells = (
    cells: readonly SurveyCellView[],
  ): readonly { readonly index: number; readonly cell: SurveyCellView }[] =>
    chartIndices.flatMap((index) => {
      const cell = cells[index];
      return cell === undefined ? [] : [{ index, cell }];
    });

  // Blue is sleep — the hours slept and the rating of the night alike — and ember is energy. The
  // vital decides where ANY snapshot of the question names one — an occurrence frozen before the
  // field existed carries none, and the same row must not change ink halfway along. The sleep
  // RATING is no vital, and is told by its code.
  const hueOf = (row: SurveyRowView): SurveySeriesHue => {
    const named = context.asksOf.map((asks) => asks.get(row.key)).find((question) => question?.vitalType != null);
    const vital = questionVital(named ?? { code: row.code });
    if (vital === 'SleepDuration' || row.code === SLEEP_CODE) return 'sleep';
    return vital === 'EnergyLevel' ? 'energy' : 'neutral';
  };

  /** The occurrences that carry a reading on this row, in order, with their dates. */
  const readingsOf = (
    cells: readonly SurveyCellView[],
  ): readonly { readonly at: Date; readonly value: number }[] =>
    windowCells(cells).flatMap(({ index, cell }) =>
      cell.state === 'value' && cell.number !== null
        ? [{ at: dateAt(index), value: cell.number }]
        : [],
    );

  const plotOf = (row: SurveyRowView, kind: 'number' | 'scale'): SurveyPlotView => {
    const readings = readingsOf(row.cells);
    const first = readings[0]?.value ?? null;
    const last = readings.at(-1)?.value ?? null;
    const change = first === null || last === null || readings.length < 2 ? null : last - first;
    // A rating's head prints the window's AVERAGE and a measured quantity's prints its newest
    // reading — see `figureText`. The average is rounded by the same formatter every other figure
    // on this tab goes through, so „3,6" is one decimal here as it is everywhere else.
    const average =
      readings.length === 0
        ? null
        : readings.reduce((total, reading) => total + reading.value, 0) / readings.length;
    const head = kind === 'scale' ? average : last;
    return {
      key: row.key,
      name: row.shortLabel,
      hue: hueOf(row),
      unit: row.unit,
      points: readings.map((reading) => ({ x: reading.at, y: reading.value })),
      figureText: head === null ? null : number.format(head),
      delta: change === null ? null : roundedDelta(change),
      deltaText: change === null ? null : signed(change, locale),
      // A 1–5 rating has a direction and it is the SCALE's own, not a guess about the trainer's
      // question: five is the top of the control, so a rating that rose is the good news. A measured
      // quantity has none this app may claim — a client cutting and a client bulking read the same
      // fall in weight in opposite ways — so its change is printed and never judged.
      deltaTone:
        change === null ? null : kind === 'scale' ? deltaTone(roundedDelta(change), false) : 'flat',
      kind,
      empty: readings.length === 0,
    };
  };

  /**
   * A two-option question as a ring over its own occurrences.
   *
   * ── The figure is the SHARE of the first option, because one answer is not a state ──────────
   * „Tak" on the last check-in says one thing about one morning. What a trainer is reading a
   * twelve-week strip for is „how often", so the figure is the share across the window — the same
   * argument that makes a rating print its average rather than its last reading.
   *
   * ── The change compares the window's two halves ─────────────────────────────────────────────
   * Every other card's delta is its newest reading minus its oldest. On a series whose answers are
   * only ever one of two words that subtraction says everything about one occurrence and nothing
   * about a trend. The same QUESTION — is it different at the end of the window than at the start —
   * is answered by comparing the first option's share over the newer half of the ANSWERED
   * occurrences against its share over the older half, in percentage points. Fewer than two
   * readings and there is no previous window to compare with, which is `null`.
   *
   * ── Nothing here is judged, and that is not caution ─────────────────────────────────────────
   * „Tak" to „piłaś dziś alkohol?" and „tak" to „zrobiłaś rozciąganie?" are opposite news, and
   * „zielony" is not news at all. Only the trainer's question says which, so the change is printed
   * in neutral — the same rule weight already follows.
   */
  const ringOf = (row: SurveyRowView, options: readonly [string, string]): SurveyRingView => {
    const [optionA, optionB] = options;
    const cells: SurveyChoiceCell[] = windowCells(row.cells).map(({ index, cell }) => {
      const state =
        cell.state !== 'value' || cell.text === null
          ? 'none'
          : cell.text === optionA
            ? 'a'
            : cell.text === optionB
              ? 'b'
              : 'none';
      return {
        state,
        title: t('web:clientDetail.surveys.choiceCell', {
          date: columns[index]?.dateLabel ?? '',
          state:
            state === 'none'
              ? t('web:clientDetail.surveys.noAnswer')
              : state === 'a'
                ? optionA
                : optionB,
        }),
      };
    });
    const answered = cells.filter((cell) => cell.state !== 'none');
    const aCount = answered.filter((cell) => cell.state === 'a').length;
    const percentOf = (part: readonly SurveyChoiceCell[]): number =>
      (part.filter((cell) => cell.state === 'a').length / part.length) * 100;
    const half = Math.floor(answered.length / 2);
    const change =
      answered.length < 2
        ? null
        : percentOf(answered.slice(half)) - percentOf(answered.slice(0, half));
    const percent = answered.length === 0 ? 0 : percentOf(answered);
    return {
      key: row.key,
      name: row.shortLabel,
      hue: hueOf(row),
      figureText:
        answered.length === 0
          ? null
          : t('web:clientDetail.surveys.optionShare', {
              value: share.format(percent),
              option: optionA,
            }),
      delta: change === null ? null : Math.round(change),
      deltaText:
        change === null
          ? null
          : t('web:clientDetail.surveys.shareDelta', {
              delta: signedWhole(Math.round(change), share),
            }),
      deltaTone: change === null ? null : 'flat',
      kind: 'binary',
      optionA,
      optionB,
      aCount,
      bCount: answered.length - aCount,
      percent,
      countText: t('web:clientDetail.surveys.countOfAnswered', {
        value: share.format(aCount),
        total: share.format(answered.length),
      }),
      cells,
      fromLabel: columns[chartIndices[0] ?? -1]?.dateLabel ?? '',
      toLabel: columns[chartIndices.at(-1) ?? -1]?.dateLabel ?? '',
      empty: answered.length === 0,
    };
  };

  const series = rows.flatMap((row): SurveySeriesView[] => {
    if (!charted(row)) return [];
    if (row.kind === 'number') return [plotOf(row, 'number')];
    if (row.kind === 'scale') return [plotOf(row, 'scale')];
    // A two-option question is charted for the same reason a rating is: the answer is one of two
    // words and the only thing a trainer can read off twelve of them is the SHAPE. Three options
    // and there is no single share to draw, so it stays a matrix row.
    if (row.binaryOptions !== null) return [ringOf(row, row.binaryOptions)];
    return [];
  });

  // The weight the composite carries, when no plain weight question was asked. It is the same
  // quantity and the trainer asked for it to be charted, so it joins the strip at the front.
  // The sub-row only exists when the form asked NO weight question of its own, so there is nothing
  // on the strip for it to duplicate — an earlier guard against a second kilogram series could
  // never be false and is gone.
  const compositeWeight = rows.find(
    (row) => row.kind === 'measurement' && row.key.endsWith(':weightKg') && charted(row),
  );
  if (compositeWeight !== undefined) series.unshift(plotOf(compositeWeight, 'number'));

  return series;
}

/** „+25" / „−25" / „0" on a whole number. The percentage-point twin of `signed`. */
function signedWhole(value: number, format: Intl.NumberFormat): string {
  const magnitude = format.format(Math.abs(value));
  // A true minus sign, not a hyphen — the same glyph every other change on this tab prints.
  return value > 0 ? `+${magnitude}` : value < 0 ? `−${magnitude}` : magnitude;
}

/**
 * The occurrences in the order every surface downstream assumes: ascending by the CYCLE each one
 * stands for.
 *
 * `api/clientDetailSource` already sorts them, and this is deliberately a second application of the
 * same total order rather than trust in the caller's. It is the one assumption the whole projection
 * rests on — the ordinal, the x domain, the delta against the previous answered column and the
 * comparison's direction are all positional — and a sort of an already-sorted list is free. A
 * missed occurrence has no `completedAt` at all, so that is only the tie-break for two cycles on one
 * date, and the id settles the rest so the order is total and stable.
 */
export function orderedOccurrences(
  occurrences: readonly SurveyOccurrenceSource[],
): readonly SurveyOccurrenceSource[] {
  return [...occurrences].sort(
    (left, right) =>
      (left.cycleDate ?? left.completedAt ?? '').localeCompare(
        right.cycleDate ?? right.completedAt ?? '',
      ) ||
      (left.completedAt ?? '').localeCompare(right.completedAt ?? '') ||
      left.id - right.id,
  );
}

/**
 * The occurrences that belong with the given one, oldest first, and the form they are of.
 *
 * A recurring form is its whole template — that is the group a report's „poprzednio" and a
 * comparison are read across. An INTAKE stands alone, and so does an occurrence the wire gave no
 * `surveyTemplateId`: it is asked once, there is no second occurrence of it to compare with, and
 * grouping it with anything else would invent a cadence it does not have. It is still readable,
 * which is the whole reason the stream lists it.
 */
function groupOf(
  ordered: readonly SurveyOccurrenceSource[],
  target: SurveyOccurrenceSource,
): readonly SurveyOccurrenceSource[] {
  if (isOneOffKind(target.kind) || target.templateId === null) return [target];
  return ordered.filter(
    (occurrence) => !isOneOffKind(occurrence.kind) && occurrence.templateId === target.templateId,
  );
}

/**
 * A form asked ONCE: the intake, and a converted lead's public-form questionnaire (public-form R21),
 * which the client's history shows as a completed questionnaire of its own — never a position in a
 * series, and never the intake. The backend's `SurveyKindRules.IsOneOff`, stated on this side.
 */
export function isOneOffKind(kind: string | null): boolean {
  return kind === 'Intake' || kind === 'LeadForm';
}

/**
 * The occurrence the tab opens on: the newest one that was actually ANSWERED, of any form.
 *
 * A missed occurrence has no answers, so a report of it is a column of dashes — opening the tab on
 * one would be opening it on nothing. Exported because `model/surveyStream` marks the same row as
 * current, and two implementations of "which one is showing" would disagree the first time one of
 * them learned about `Cancelled`.
 *
 * ── And it has to be a row the STREAM drew ───────────────────────────────────────────────────
 * An occurrence with no `cycleDate` cannot be placed in a month, so the stream does not list it.
 * Opening the tab on one put the pane on a report no row on the left pointed at, and nothing the
 * trainer could press brought it back. Same predicate as the stream's own filter, stated once.
 */
export function newestAnsweredOccurrenceId(source: SurveyHistorySource | null): number | null {
  if (source === null) return null;
  return (
    [...orderedOccurrences(source.occurrences)]
      .reverse()
      .find((occurrence) => occurrence.status === 'Completed' && occurrence.cycleDate !== null)
      ?.id ?? null
  );
}

/** A form that is SENT on a schedule, and therefore has a trend to draw. An intake has neither. */
function isCadenced(occurrence: SurveyOccurrenceSource | undefined): boolean {
  return occurrence !== undefined && !isOneOffKind(occurrence.kind) && occurrence.frequency !== null;
}

/**
 * **Which occurrence the CHART STRIP is drawn over.** Owner, 16 September 2026.
 *
 * The strip follows the form the pane is showing — but only while that form has a cadence. An
 * intake is asked once: charting it is a plot of one point, and letting it take the strip threw
 * away the very trend the trainer opened the tab to read. So selecting a cadenceless occurrence
 * leaves the strip where it was, on the last cadenced form it showed.
 *
 * @param lastCadencedId the cadenced occurrence the strip was last moved to, or `null` before the
 *   trainer has moved it. Its cadence is re-checked here rather than trusted: this is the one rule,
 *   and a caller that remembered the wrong id must not be able to put a one-off form on the strip.
 */
export function chartStripOccurrenceId(
  source: SurveyHistorySource | null,
  selectedId: number | null,
  lastCadencedId: number | null,
): number | null {
  if (source === null) return null;
  const ordered = orderedOccurrences(source.occurrences);
  const at = (id: number | null): SurveyOccurrenceSource | undefined =>
    id === null ? undefined : ordered.find((occurrence) => occurrence.id === id);

  const selected = at(selectedId ?? newestAnsweredOccurrenceId(source));
  if (isCadenced(selected)) return selected?.id ?? null;
  if (isCadenced(at(lastCadencedId))) return lastCadencedId;

  // Nothing remembered — the trainer's first press was on the intake. The strip opens on what it
  // would have opened on anyway: the newest answered occurrence of a form that HAS a series. With
  // no such form at all it stays on the selection, because an empty strip beside a readable report
  // says the read failed.
  const newest = [...ordered]
    .reverse()
    .find(
      (occurrence) =>
        isCadenced(occurrence) &&
        occurrence.status === 'Completed' &&
        occurrence.cycleDate !== null,
    );
  return newest?.id ?? selected?.id ?? null;
}

export function buildSurveyHistory(
  source: SurveyHistorySource | null,
  deps: SurveyHistoryDeps,
): SurveyHistoryView {
  const { t, locale, chartRange, occurrenceId, exerciseNames } = deps;

  const empty: SurveyHistoryView = {
    activeForm: null,
    columns: [],
    selected: null,
    rows: [],
    series: [],
    xDomain: null,
    isTruncated: source?.isTruncated ?? false,
    empty: true,
    photoUrlsExpireAt: null,
  };
  if (source === null) return empty;

  /* DATED occurrences only, which is the stream's own filter. Everything below is positional — the
   * ordinal, the x domain, the delta chain — and an occurrence with no `cycleDate` has no place in
   * any of them: it cannot be put in a month, so the stream never lists it, and a column built for
   * one asked `Intl` to format an invalid date. */
  const ordered = orderedOccurrences(source.occurrences).filter(
    (occurrence) => occurrence.cycleDate !== null,
  );
  const wanted = occurrenceId ?? newestAnsweredOccurrenceId(source);
  // Falling back to the newest occurrence of ALL covers the one client whose every survey was
  // missed: there is nothing answered to open on, and a report of dashes is still the honest
  // answer to "what did we ask her".
  const target =
    ordered.find((occurrence) => occurrence.id === wanted) ?? ordered.at(-1) ?? null;
  if (target === null) return empty;

  const visible = groupOf(ordered, target);
  const columns = buildColumns(visible, { t, locale });

  /* The occurrences the CHARTS are drawn over: the range bar's window, and nothing else on this
   * tab reads it. Indices rather than a filtered list, because every projection below is positional
   * — a cell is found by its column's index, and a second array would have to be kept in step. */
  const chartIndices = visible.flatMap((occurrence, index) => {
    const date = occurrence.cycleDate;
    return date !== null && date >= chartRange.from && date <= chartRange.to ? [index] : [];
  });

  const context: SurveyBuildContext = {
    t,
    locale,
    number: readingFormat(locale),
    dayMonth: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }),
    exerciseNames,
    visible,
    columns,
    chartIndices,
    answersOf: visible.map(
      (occurrence) => new Map(occurrence.answers.map((answer) => [answer.key, answer])),
    ),
    asksOf: visible.map(
      (occurrence) => new Map(occurrence.questions.map((question) => [question.key, question])),
    ),
  };

  const rows = buildRows(context);
  const firstDate = visible[chartIndices[0] ?? -1]?.cycleDate ?? null;
  const lastDate = visible[chartIndices.at(-1) ?? -1]?.cycleDate ?? null;

  return {
    activeForm: { id: target.templateId ?? target.id, title: visible.at(-1)?.title ?? '' },
    columns,
    selected: columns.find((column) => column.id === target.id) ?? null,
    rows,
    series: buildSeries(context, rows),
    xDomain:
      firstDate === null || lastDate === null
        ? null
        : [new Date(`${firstDate}T00:00:00Z`), new Date(`${lastDate}T00:00:00Z`)],
    isTruncated: source.isTruncated,
    empty: false,
    photoUrlsExpireAt:
      visible
        .flatMap((occurrence) =>
          occurrence.photoUrlsExpireAt === null ? [] : [occurrence.photoUrlsExpireAt],
        )
        .sort((left, right) => left.localeCompare(right))[0] ?? null,
  };
}
